import { useCallback, useRef } from 'react'
import { useTagsStore } from '../store/tagsStore'
import { useNotesStore } from '../store/notesStore'

export const useSimpleHashtags = () => {
  const { addTagToNote, removeTagFromNote, createTag, tags } = useTagsStore()
  const { refreshNotes } = useNotesStore()
  const debounceRef = useRef<NodeJS.Timeout>()

  // Extract hashtags from plain text content
  const extractHashtagsFromText = useCallback((content: string): string[] => {
    const matches: string[] = []
    
    // Split content into lines to properly detect markdown headers
    const lines = content.split('\n')
    
    for (const line of lines) {
      const trimmedLine = line.trim()
      
      // Skip lines that start with markdown headers (# followed by space)
      if (trimmedLine.match(/^#+\s/)) {
        continue
      }
      
      // Skip empty lines
      if (!trimmedLine) {
        continue
      }
      
      // Match # followed by word characters (2+ chars), ensuring proper word boundaries
      // Look for # at start of line, after whitespace, or after non-word characters
      const hashtagRegex = /(?:^|[\s\W])#(\w{2,})(?=\s|$|[^\w])/g
      
      // Find hashtags in this line
      let match
      while ((match = hashtagRegex.exec(line)) !== null) {
        matches.push(match[1])
      }
    }

    return [...new Set(matches)] // Remove duplicates
  }, [])

  // Sync hashtags from note content to database
  const syncNoteTags = useCallback(async (noteId: string, content: string) => {
    if (!noteId) return

    try {
      // Extract hashtags from current content
      const currentHashtags = extractHashtagsFromText(content)
      
      // Get existing tags for this note
      const { getTagsForNote } = useTagsStore.getState()
      const existingTags = await getTagsForNote(noteId)
      const existingTagNames = existingTags.map(tag => tag.name)

      // Find tags to add and remove
      const tagsToAdd = currentHashtags.filter(tagName => !existingTagNames.includes(tagName))
      const tagsToRemove = existingTagNames.filter(tagName => !currentHashtags.includes(tagName))

      // Remove tags that are no longer in content
      for (const tagName of tagsToRemove) {
        const tag = existingTags.find(t => t.name === tagName)
        if (tag) {
          await removeTagFromNote(noteId, tag.id)
        }
      }

      // Add new tags
      for (const tagName of tagsToAdd) {
        // Find existing tag or create new one
        let tag = tags.find(t => t.name === tagName)
        if (!tag) {
          const newTag = await createTag(tagName)
          if (newTag) {
            tag = newTag
          }
        }
        
        if (tag && tag.id) {
          await addTagToNote(noteId, tag.id)
        }
      }
      
      // Refresh notes list to show updated tag associations
      await refreshNotes()
    } catch (error) {
      console.error('Failed to sync note tags:', error)
    }
  }, [tags, addTagToNote, removeTagFromNote, createTag, extractHashtagsFromText, refreshNotes])

  // Debounced version that waits for user to stop typing
  const debouncedSyncNoteTags = useCallback((noteId: string, content: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    
    debounceRef.current = setTimeout(() => {
      syncNoteTags(noteId, content)
    }, 300)
  }, [syncNoteTags])

  return {
    extractHashtagsFromText,
    syncNoteTags,
    debouncedSyncNoteTags
  }
}